// =====================================================================
// src/components/ImageViewerModal.jsx
//
// Fullscreen photo viewer with tap-to-zoom. Shared between merchant
// and customer surfaces — anywhere a product image (or future feature
// image) needs to be opened at full resolution.
//
// Used in:
//   - MerchantApp catalog (tap product image in catalog list)
//   - BusinessProfile customer catalog (tap product image in product card)
//   - (future) Cart line item photos, OrderDetail item photos, chat photos
//
// UX:
//   - Default: image fits viewport via object-fit: contain
//   - Tap image once → zoom to natural pixel size, container becomes
//     scrollable so the user can pan around
//   - Tap image again → fit back to viewport
//   - Tap backdrop / × / Esc → close
//
// Why not pinch-zoom: Capacitor and most mobile-web apps disable
// the page's user-scalable viewport, so true native pinch is
// unavailable inside the modal. Click-to-toggle + scroll-pan gives
// a predictable UX without relying on viewport gestures.
//
// CSS lives in ImageViewerModal.css so any consumer just imports
// this file and gets the styles for free.
// =====================================================================

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import './ImageViewerModal.css';

export default function ImageViewerModal({ open, src, onClose }) {
  const [zoomed, setZoomed] = useState(false);

  // Reset zoom every time the modal opens; otherwise the next image
  // would inherit the previous one's zoom state.
  useEffect(() => {
    if (!open) setZoomed(false);
  }, [open]);

  // Esc to close. Keyboard a11y for desktop users; mobile users
  // tap the backdrop or ×.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="iv-backdrop" onClick={onClose}>
      <button
        type="button"
        className="iv-close"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        aria-label="Close"
      >
        <X size={20} />
      </button>
      <div
        className={`iv-scroll${zoomed ? ' zoomed' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt=""
          className="iv-img"
          onClick={() => setZoomed(z => !z)}
        />
      </div>
      <div className="iv-hint">
        {zoomed ? 'Tap to fit · Tap outside to close' : 'Tap to zoom · Tap outside to close'}
      </div>
    </div>
  );
}
